import { useState } from "react";
import { CalendarPlus } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { clinicScope } from "@/lib/scope";
import { uuid } from "@/lib/format";
import { today, live, SPECIALTIES } from "@/lib/clinic";
import { BigButton, Field, inputCls } from "@/components/ui-kit";
import { isBlocked, useBlocks } from "./BlockDialog";

export default function AppointmentForm({ patient, onSaved, compact, initial }) {
  const { t, dispatch, view } = useApp();
  const doctors = view.staff.filter((s) => s.active && ["doctor", "owner"].includes(s.role));
  const patients = live(view.patients);
  const scope = clinicScope(view.shop);
  const [f, setF] = useState({ patient_id: patient?.id || "", date: today(), time: new Date().toTimeString().slice(0, 5), doctor_id: doctors[0]?.id || "", reason: "", specialty: scope || "", ...(initial || {}) });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const [blocks] = useBlocks();
  const save = () => {
    const p = patient || patients.find((x) => x.id === f.patient_id);
    if (!p) return toast.error(t("select_patient"));
    const doc = doctors.find((d) => d.id === f.doctor_id);
    const data = { id: uuid(), patient_id: p.id, patient_name: p.name, doctor_id: doc?.id || "", doctor_name: doc?.name || "", specialty: f.specialty || doc?.specialty || "", date: f.date, time: f.time, reason: f.reason.trim(), status: f.date === today() ? "waiting" : "scheduled" };
    if (isBlocked(blocks, data.doctor_id, data.date, data.time)) return toast.error(t("slot_blocked"));
    if (dispatch("appointment.upsert", data)) { toast.success(t("appointment_saved")); onSaved?.(data); }
  };
  return (
    <div className={`grid gap-2 ${compact ? "grid-cols-2 lg:grid-cols-7" : "sm:grid-cols-2 lg:grid-cols-3"}`} data-testid="appointment-form">
      {!patient && (
        <Field label={t("patient")}>
          <select value={f.patient_id} onChange={set("patient_id")} className={inputCls} data-testid="appt-patient-select"><option value="">—</option>{patients.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select>
        </Field>
      )}
      <Field label={t("hr_date")}><input type="date" dir="ltr" value={f.date} onChange={set("date")} className={inputCls} data-testid="appt-date-input" /></Field>
      <Field label={t("time")}><input type="time" dir="ltr" value={f.time} onChange={set("time")} className={inputCls} data-testid="appt-time-input" /></Field>
      <Field label={t("specialty")}>
        <select value={f.specialty} onChange={set("specialty")} disabled={!!scope} className={inputCls} data-testid="appt-specialty-select"><option value="">—</option>{(scope ? [scope] : SPECIALTIES).map((s) => <option key={s} value={s}>{t(`sp_${s}`)}</option>)}</select>
      </Field>
      <Field label={t("doctor")}>
        <select value={f.doctor_id} onChange={set("doctor_id")} className={inputCls} data-testid="appt-doctor-select"><option value="">—</option>{doctors.filter((d) => !f.specialty || !d.specialty || d.specialty === f.specialty).map((d) => <option key={d.id} value={d.id}>{d.name}{d.specialty && ` · ${t(`sp_${d.specialty}`)}`}</option>)}</select>
      </Field>
      <Field label={t("reason")}><input value={f.reason} onChange={set("reason")} className={inputCls} data-testid="appt-reason-input" /></Field>
      <div className="flex items-end"><BigButton onClick={save} className="w-full !h-12" data-testid="appt-save-btn"><CalendarPlus className="w-5 h-5" />{t("book")}</BigButton></div>
    </div>
  );
}
